import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getClaimsByPatient } from '@/services/orderReturns';
import {
	createPatient,
	getPatient,
	getPatients,
	getPatientsAi,
	getPatientsWithApprovedBV,
	getPatientsWithApprovedBVAi,
	updatePatient,
} from '@/services/patients';
import type { PatientClaimRow, Patients } from '@/types/patient';
import type { FilterType } from '@/types/table';

export const useGetPatients = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'patients',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getPatients(
				page,
				limit,
				columnsFilters,
				search,
				status as string,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetPatientsAi = ({
	page = 1,
	limit = 15,
	enabled = true,
	clinicId,
	search = '',
}: FilterType) => {
	return useQuery({
		queryKey: ['patients-ai', page, limit, search, clinicId],
		queryFn: () => getPatientsAi(page, limit, search, clinicId),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetPatient = (id: string | null, enabled = true) => {
	return useQuery({
		queryKey: ['patient', id],
		queryFn: () => getPatient(id as string),
		enabled: enabled && !!id,
		keepPreviousData: false,
	});
};

export const useGetClaims = (id: string | null, enabled = true) => {
	return useQuery<PatientClaimRow[]>({
		queryKey: ['claims', id],
		queryFn: () => getClaimsByPatient(id as string),
		enabled: enabled && !!id,
		keepPreviousData: false,
	});
};

export const useCreatePatient = (
	onSuccess?: (data: Patients, payload: any) => void,
	onError?: (error: any) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (patientData: any) => createPatient(patientData),
		onSuccess: (data, payload) => {
			queryClient.invalidateQueries({ queryKey: ['patients'] });
			onSuccess?.(data, payload);
		},
		onError: (error) => {
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to create patient. Please try again.',
			);
			onError?.(error);
		},
	});
};

export const useUpdatePatient = (
	onSuccess?: () => void,
	onError?: (error: any) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, patientData }: { id: string; patientData: any }) =>
			updatePatient(id, patientData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['patients'] });
			queryClient.invalidateQueries({ queryKey: ['patient', variables.id] });
			onSuccess?.();
		},
		onError: (error) => {
			console.log('error', error);
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to update patient. Please try again.',
			);
			onError?.(error);
		},
	});
};

export const useGetPatientsWithApprovedBV = ({
	page = 1,
	limit = 50,
	enabled = true,
	search = '',
	sort = '',
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'patients-bv-approved',
			page,
			limit,
			search,
			sort,
			globalFilters,
		],
		queryFn: () =>
			getPatientsWithApprovedBV(
				page,
				limit,
				search,
				sort ?? '',
				globalFilters as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetPatientsWithApprovedBVAi = ({
	page = 1,
	limit = 50,
	enabled = true,
	search = '',
	clinicId = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'patients-bv-approved-ai',
			page,
			limit,
			search,
			clinicId
		],
		queryFn: () =>
			getPatientsWithApprovedBVAi(
				page,
				limit,
				search,
				clinicId
			),
		enabled,
		keepPreviousData: false,
	});
};
